import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import i18n from "../i18n";
import { registrationApi } from "../services/saasApi";
import { texts } from "../components/login/loginConstants";

import LoginLanguageSelector from "../components/login/LoginLanguageSelector";
import LoginTrialExpiredSection from "../components/login/LoginTrialExpiredSection";
import LoginFormCard from "../components/login/LoginFormCard";
import LoginFooter from "../components/login/LoginFooter";
import Seo from "../components/Seo";

const Login = () => {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginProgress, setLoginProgress] = useState(0);
  const [language, setLanguage] = useState(i18n.language?.split("-")[0] || "fr");
  const [trialExpiredData, setTrialExpiredData] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ username: false, password: false });

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const t = texts[language] || texts.fr;

  useEffect(() => {
    if (isAuthenticated) navigate("/dashboard");
  }, [isAuthenticated, navigate]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  };

  const handleSubscribe = async (planCode, gateway) => {
    const subdomain = window.location.hostname.split(".")[0];
    setPaymentLoading(`${planCode}-${gateway}`);
    setPaymentError(null);
    try {
      const payload = { subdomain, plan_code: planCode, billing_cycle: billingCycle, admin_email: "", admin_first_name: "", admin_last_name: "" };
      const result = gateway === "stripe"
        ? await registrationApi.initiateStripePayment(payload)
        : await registrationApi.initiatePayment(payload);
      if (result.checkout_url) window.location.href = result.checkout_url;
      if (result.payment_url) window.location.href = result.payment_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.originalError?.response?.data?.detail || err?.message || t.paymentInitError;
      setPaymentError(msg);
      setSelectedPlan(null);
    } finally {
      setPaymentLoading(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: false }));
    if (error) setError("");
  };

  const getPasswordStrength = (password) => {
    if (!password) return 0;
    if (password.length < 4) return 25;
    if (password.length < 6) return 50;
    if (password.length < 8) return 75;
    return 100;
  };

  const validateForm = () => {
    const errors = { username: !credentials.username.trim(), password: !credentials.password.trim() };
    setFieldErrors(errors);
    return !errors.username && !errors.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({ username: false, password: false });
    if (!validateForm()) { setError(t.requiredField); return; }

    setLoading(true);
    setLoginProgress(0);
    const progressInterval = setInterval(() => setLoginProgress((p) => Math.min(p + 20, 90)), 100);

    try {
      await login(credentials.username, credentials.password);
      setLoginProgress(100);
      setTimeout(() => { clearInterval(progressInterval); navigate("/dashboard"); }, 500);
    } catch (error) {
      clearInterval(progressInterval);
      setLoginProgress(0);
      const responseObj = error.response ?? error.originalError?.response;
      const httpStatus = responseObj?.status;
      const detail = responseObj?.data?.detail;
      if (httpStatus === 402 && detail && typeof detail === "object") { setTrialExpiredData(detail); return; }
      setError(typeof detail === "string" ? detail : t.invalidCredentials);
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const passwordStrength = getPasswordStrength(credentials.password);

  return (
    <>
      <Seo
        title="Connexion — Mawered SIRH"
        description="Connectez-vous à votre espace Mawered."
        path="/login"
        noindex
      />
    <div
      className="dark min-h-screen flex items-center justify-center p-4"
      dir={language === "ar" ? "rtl" : "ltr"}
      style={{ backgroundColor: "var(--lm-canvas)", fontFamily: "Inter, system-ui, sans-serif", position: "relative", overflow: "hidden" }}
    >
      {/* Ambient glow orbs */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
        <div style={{
          position: "absolute", top: "-15%", left: "50%", transform: "translateX(-50%)",
          width: "600px", height: "400px",
          background: "radial-gradient(ellipse, rgba(94,106,210,0.10) 0%, transparent 70%)",
          filter: "blur(70px)",
        }} />
        <div style={{
          position: "absolute", bottom: "-10%", right: "10%",
          width: "400px", height: "300px",
          background: "radial-gradient(ellipse, rgba(94,106,210,0.06) 0%, transparent 70%)",
          filter: "blur(80px)",
        }} />
      </div>
      <div className={`w-full transition-all duration-300 ${trialExpiredData ? "max-w-2xl" : "max-w-sm"}`}>
        <LoginLanguageSelector language={language} changeLanguage={changeLanguage} />

        {trialExpiredData && (
          <LoginTrialExpiredSection
            trialExpiredData={trialExpiredData}
            t={t}
            language={language}
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            paymentLoading={paymentLoading}
            paymentError={paymentError}
            handleSubscribe={handleSubscribe}
            setTrialExpiredData={setTrialExpiredData}
          />
        )}

        {!trialExpiredData && (
          <LoginFormCard
            t={t}
            language={language}
            loading={loading}
            loginProgress={loginProgress}
            error={error}
            credentials={credentials}
            handleChange={handleChange}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            fieldErrors={fieldErrors}
            handleSubmit={handleSubmit}
            passwordStrength={passwordStrength}
          />
        )}

        <LoginFooter t={t} />
      </div>
    </div>
    </>
  );
};

export default Login;
